import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import type { SxProps, Theme } from '@mui/material/styles'

export interface ActionButtonProps {
    text: string
    onClick?: () => void
    disabled?: boolean
    variant?: 'default' | 'dark'
    type?: 'button' | 'submit' | 'reset'
    loading?: boolean
    startIcon?: React.ReactNode
    /** Override default width (270). Accepts number (px) or any CSS length (e.g. '100%', '175px'). */
    width?: number | string
    /** Override default height (48). Accepts number (px) or any CSS length. */
    height?: number | string
    /** Override default font size (20). Accepts number (px) or any CSS length. */
    fontSize?: number | string
    /** Override default font weight (600). */
    fontWeight?: number | string
    sx?: SxProps<Theme>
    'data-testid'?: string
}

const styles = {
    // Disabled state (#ccdeef bg + white text) is handled globally by the theme's
    // MuiButtonBase override, so variants only set their enabled + hover colors.
    default: {
        // Figma 26:154985 — Primary-blue #5a8fc3.
        bgcolor: '#5a8fc3',
        '&:hover': { boxShadow: 'none', bgcolor: '#4a7fb4' }
    },
    dark: {
        bgcolor: '#0b3b61',
        '&:hover': { boxShadow: 'none', bgcolor: '#092e4d' }
    }
} as const

export const ActionButton = ({
    text,
    onClick,
    disabled = false,
    variant = 'default',
    type = 'button',
    loading = false,
    startIcon,
    width = 270,
    height = 48,
    fontSize = 20,
    fontWeight = 600,
    sx,
    'data-testid': dataTestId = 'action-button-button-on-click'
}: ActionButtonProps) => (
    <Button
        variant="contained"
        size="large"
        type={type}
        onClick={onClick}
        disabled={disabled || loading}
        startIcon={startIcon}
        sx={[
            {
                width,
                height,
                fontSize,
                borderRadius: '99px',
                fontFamily: "'Poppins', 'NotoSansThai', 'Noto Sans Thai', sans-serif",
                fontWeight,
                letterSpacing: '-0.2px',
                textTransform: 'none',
                boxShadow: 'none',
                ...styles[variant]
            },
            ...(Array.isArray(sx) ? sx : sx ? [sx] : [])
        ]}
        data-testid={dataTestId}>
        {loading ? <CircularProgress size={22} color="inherit" data-testid={`${dataTestId}-spinner`} /> : text}
    </Button>
)
